import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, BookOpen, X } from 'lucide-react';
import { Quote, ContentCategory } from '@brovicms/models';
import { quoteService, QuoteFormData } from '../../services/quoteService';
import ebookService from '../../services/ebookService';
import toast from 'react-hot-toast';
import { CONTENT_CATEGORIES } from '../../constants/categories';

interface QuoteFormProps {
  quote?: Quote;
  onSuccess: (success: boolean) => void;
  loading?: boolean;
}

const QuoteForm: React.FC<QuoteFormProps> = ({ quote, onSuccess, loading = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBook, setSelectedBook] = useState<{ id: string; title: string } | null>(null);
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [books, setBooks] = useState<{ id: string; title: string }[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm<QuoteFormData>({
    defaultValues: {
      content: '',
      source: '',
      category: {
        id: '',
        name: '',
        slug: '',
        isActive: true,
        sortOrder: 0
      },
      tags: [],
      language: 'en',
      isPublished: false,
      isFeatured: false,
      isPremium: false
    }
  });

  const watchedValues = watch();

  useEffect(() => {
    if (quote) {
      reset({
        content: quote.content,
        source: quote.source,
        category: quote.category,
        tags: quote.tags,
        language: quote.language,
        isPublished: quote.isPublished,
        isFeatured: quote.isFeatured,
        isPremium: quote.isPremium,
        bookReference: quote.bookReference
      });
      
      if (quote.bookReference) {
        setSelectedBook({
          id: quote.bookReference.bookId,
          title: quote.bookReference.bookTitle
        });
      }
    }
  }, [quote, reset]);

  // Load categories and books
  useEffect(() => {
    // Load categories from constants
    setCategories(CONTENT_CATEGORIES);

    // Load e-books from database
    const loadBooks = async () => {
      try {
        const result = await ebookService.getEBooks({ limit: 50 });
        if (result.data) {
          const bookList = result.data.map(book => ({
            id: book.id,
            title: book.title
          }));
          setBooks(bookList);
        }
      } catch (error) {
        console.error('Error loading books:', error);
        // Fallback to mock data if service fails
        setBooks([
          { id: '1', title: 'The Power of Now' },
          { id: '2', title: 'Atomic Habits' },
          { id: '3', title: 'Think and Grow Rich' },
          { id: '4', title: 'The 7 Habits of Highly Effective People' }
        ]);
      }
    };

    loadBooks();
  }, []);

  const onSubmitWithValidation = async (data: QuoteFormData) => {
    setIsSubmitting(true);
    try {
      if (quote) {
        // Update existing quote
        const result = await quoteService.updateQuote(quote.id, data);
        if (result.success) {
          toast.success('Quote updated successfully');
          onSuccess(true);
        } else {
          toast.error(result.error || 'Failed to update quote');
        }
      } else {
        // Create new quote
        const result = await quoteService.createQuote(data);
        if (result.success) {
          toast.success('Quote created successfully');
          onSuccess(true);
        } else {
          toast.error(result.error || 'Failed to create quote');
        }
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      toast.error('An error occurred while saving the quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookSelect = (book: { id: string; title: string }) => {
    setSelectedBook(book);
    setValue('bookReference', {
      bookId: book.id,
      bookTitle: book.title,
      pageNumber: undefined,
      chapter: undefined
    });
  };

  const removeBookReference = () => {
    setSelectedBook(null);
    setValue('bookReference', undefined);
  };

  const addTag = (tag: string) => {
    const currentTags = watchedValues.tags || [];
    if (tag.trim() && !currentTags.includes(tag.trim())) {
      setValue('tags', [...currentTags, tag.trim()]);
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = watchedValues.tags || [];
    setValue('tags', currentTags.filter(tag => tag !== tagToRemove));
  };

  const togglePublished = () => {
    setValue('isPublished', !watchedValues.isPublished);
  };

  const toggleFeatured = () => {
    setValue('isFeatured', !watchedValues.isFeatured);
  };

  const togglePremium = () => {
    setValue('isPremium', !watchedValues.isPremium);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {quote ? 'Edit Quote' : 'Add New Quote'}
        </h2>
        <p className="text-gray-600 mt-1">
          {quote ? 'Update quote details' : 'Create a new inspirational quote'}
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Quote Details */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quote Details</h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="form-label">Quote Text *</label>
              <textarea
                {...register('content', { required: 'Quote content is required' })}
                rows={4}
                className="form-input"
                placeholder="Enter the quote text"
              />
              {errors.content && (
                <p className="text-red-500 text-sm mt-1">{errors.content.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Author/Source</label>
              <input
                {...register('source')}
                type="text"
                className="form-input"
                placeholder="Author or source of the quote"
              />
            </div>

          </div>
        </div>

        {/* Book Reference */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Book Reference
            </h3>
          </div>
          <div className="card-body space-y-4">
            {selectedBook ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-apple">
                <div>
                  <p className="font-medium text-gray-900">{selectedBook.title}</p>
                  <p className="text-sm text-gray-500">Linked to book</p>
                </div>
                <button
                  type="button"
                  onClick={removeBookReference}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="form-label">Link to Book (Optional)</label>
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {books.map((book) => (
                    <button
                      key={book.id}
                      type="button"
                      onClick={() => handleBookSelect(book)}
                      className="text-left p-2 hover:bg-gray-50 rounded-apple border border-gray-200"
                    >
                      <p className="font-medium text-gray-900">{book.title}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedBook && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Page Number</label>
                  <input
                    {...register('bookReference.pageNumber')}
                    type="number"
                    className="form-input"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="form-label">Chapter</label>
                  <input
                    {...register('bookReference.chapter')}
                    type="text"
                    className="form-input"
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Category */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Category</h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="form-label">Category *</label>
              <select
                value={watchedValues.category?.id || ""}
                onChange={(e) => {
                  const selectedCategory = CONTENT_CATEGORIES.find(cat => cat.id === e.target.value);
                  if (selectedCategory) {
                    setValue('category', {
                      id: selectedCategory.id,
                      name: selectedCategory.name,
                      slug: selectedCategory.slug,
                      isActive: selectedCategory.isActive,
                      sortOrder: selectedCategory.sortOrder,
                    });
                  }
                }}
                className="form-select"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => onSuccess(false)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmitWithValidation)}
            disabled={isSubmitting || loading}
            className="btn btn-primary"
          >
            {isSubmitting ? (
              'Saving...'
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {quote ? 'Update Quote' : 'Create Quote'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
